import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Volume2, VolumeX, Bot } from 'lucide-react'
import { getSessionSafe } from '../lib/supabase'
import useTaskStore from '../store/taskStore'
import { useT, useLang } from '../i18n/useT'
import { speakWithSynth } from './chat/tts'
import {
  fetchConversationList, fetchConversation, deleteConversationRow,
} from './chat/conversations'
import ChatHistory from './chat/ChatHistory'
import ChatMessages from './chat/ChatMessages'
import ChatInput from './chat/ChatInput'

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null

export default function ChatAgentModal({ onClose, autoListen, initialMessage }) {
  const t = useT()
  const lang = useLang()

  // ── State ────────────────────────────────────────────────
  const [messages, setMessages]           = useState([])
  const [input, setInput]                 = useState('')
  const [loading, setLoading]             = useState(false)
  const [listening, setListening]         = useState(false)
  const [interim, setInterim]             = useState('')
  const [ttsEnabled, setTtsEnabled]       = useState(true)
  const [conversationId, setConversationId] = useState(null)
  const [conversations, setConversations] = useState([])
  const [showHistory, setShowHistory]     = useState(false)

  const recRef       = useRef(null)
  const timeoutRef   = useRef(null)   // auto-stop dopo MAX_RECORDING_MS
  const synthRef     = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null)

  // Refs to avoid stale closures
  const messagesRef       = useRef(messages)
  const conversationIdRef = useRef(conversationId)
  const loadingRef        = useRef(loading)
  const ttsEnabledRef     = useRef(ttsEnabled)
  messagesRef.current       = messages
  conversationIdRef.current = conversationId
  loadingRef.current        = loading
  ttsEnabledRef.current     = ttsEnabled

  // Durata massima registrazione push-and-hold (stile WhatsApp)
  const MAX_RECORDING_MS = 15_000

  // ── Load conversation list on mount ──────────────────────
  useEffect(() => {
    fetchConversationList().then(setConversations)
  }, [])

  // ── Auto-send initial message (no auto-listen: press-and-hold only) ─
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    if (mounted) return
    setMounted(true)
    if (initialMessage) sendMessage(initialMessage)
    // autoListen è stato volontariamente disabilitato: il mic ora è push-to-talk.
  }, [mounted]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cleanup on unmount ───────────────────────────────────
  useEffect(() => {
    return () => {
      recRef.current?.abort?.()
      recRef.current?.stop?.()
      clearTimeout(timeoutRef.current)
      synthRef.current?.cancel()
    }
  }, [])

  // ── Conversation handlers ────────────────────────────────
  const handleLoadConversation = async (convId) => {
    const conv = await fetchConversation(convId)
    if (conv) {
      setConversationId(conv.id)
      setMessages(conv.messages || [])
      setShowHistory(false)
    }
  }

  const startNewConversation = () => {
    setConversationId(null)
    setMessages([])
    setShowHistory(false)
  }

  const handleDeleteConversation = async (convId) => {
    await deleteConversationRow(convId)
    setConversations(prev => prev.filter(c => c.id !== convId))
    if (convId === conversationId) startNewConversation()
  }

  // ── TTS helper ────────────────────────────────────────────
  const speak = useCallback((text, onEnd) => {
    speakWithSynth(synthRef.current, text, lang, onEnd)
  }, [lang])

  const toggleTts = () => {
    if (ttsEnabled) synthRef.current?.cancel()
    setTtsEnabled(prev => !prev)
  }

  // ── Send message to agent ────────────────────────────────
  const sendMessage = async (text) => {
    const msg = text.trim()
    if (!msg || loadingRef.current) return

    const prevMessages = messagesRef.current
    const newMessages = [...prevMessages, { role: 'user', content: msg }]
    setMessages(newMessages)
    setInput('')
    setLoading(true)

    try {
      const session = await getSessionSafe()
      if (!session) throw new Error('Session expired')

      const convId = conversationIdRef.current
      const toSend = convId
        ? [{ role: 'user', content: msg }]
        : newMessages.filter(m => m.role === 'user').map(m => ({ role: 'user', content: m.content }))

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-chat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type':  'application/json',
          },
          body: JSON.stringify({ messages: toSend, conversationId: convId }),
        }
      )

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Errore server')

      const reply = data.reply || t('agent_no_response')
      setMessages([...newMessages, { role: 'assistant', content: reply }])

      if (data.conversationId) setConversationId(data.conversationId)

      // Le modifiche dell'agent (create/update/complete/delete/eisenhower) di norma
      // arrivano via Supabase Realtime. Ma Realtime può essere fragile (tab in
      // background → websocket chiuso, rete flaky, RLS su listen). Se vediamo
      // un write-tool nelle actions, forziamo anche una fetch del DB come
      // fallback. `fetchTasks/Scadenze` hanno già un timeout di 5s interno:
      // in worst case non freezano la UI, ed è idempotente con l'eventuale
      // evento realtime (riconcilia l'array per id).
      const WRITE_TOOLS = new Set([
        'create_task', 'update_task', 'complete_task', 'delete_task',
        'classify_eisenhower',
      ])
      const hadWrite = Array.isArray(data.actions)
        && data.actions.some(a => WRITE_TOOLS.has(a?.tool))
      if (hadWrite) {
        const store = useTaskStore.getState()
        // Fire-and-forget: non attendiamo né blocchiamo il TTS che segue.
        store.fetchTasks?.()
        store.fetchScadenze?.()
      }

      // TTS: leggi la risposta ad alta voce, nessun auto-restart mic (push-to-talk only)
      if (ttsEnabledRef.current && synthRef.current) {
        speak(reply)
      }

      // Refresh conversation list
      fetchConversationList().then(setConversations)
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('agent_error_generic'),
      }])
    } finally {
      setLoading(false)
    }
  }

  const send = useCallback((text) => {
    const msg = text || input
    if (msg) sendMessage(msg)
  }, [input]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── SpeechRecognition push-to-talk (WhatsApp-style) ───────
  // Il mic si attiva quando l'utente tiene premuto il pulsante e si ferma
  // quando rilascia. Nessun silence timeout: stop = al rilascio oppure al massimo
  // MAX_RECORDING_MS di sicurezza.
  const startListening = useCallback(() => {
    if (!SpeechRecognitionAPI || listening) return

    synthRef.current?.cancel()

    const isAndroid = /Android/i.test(navigator.userAgent)
    const rec = new SpeechRecognitionAPI()
    rec.lang           = lang === 'it' ? 'it-IT' : 'en-US'
    rec.continuous     = !isAndroid  // Android continuous mode is buggy
    rec.interimResults = true
    rec.maxAlternatives = 1
    recRef.current     = rec

    let finalTranscript = ''

    rec.onstart = () => {
      setListening(true)
      setInterim('')
      // Safety: auto-stop dopo il massimo consentito, anche se l'utente non rilascia
      timeoutRef.current = setTimeout(() => rec.stop(), MAX_RECORDING_MS)
    }

    rec.onresult = (e) => {
      if (isAndroid) {
        const last = e.results[e.results.length - 1]
        const text = last[0].transcript.trim()
        if (last.isFinal) {
          finalTranscript = text
          setInput(text)
          setInterim('')
        } else {
          setInterim(text)
        }
        return
      }

      // Desktop/iOS: continuous=true, accumulate from resultIndex
      let newFinal = ''
      let lastInterim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) newFinal += e.results[i][0].transcript
        else lastInterim = e.results[i][0].transcript
      }
      if (newFinal) {
        finalTranscript = (finalTranscript + ' ' + newFinal).trim()
        setInput(finalTranscript)
        setInterim('')
      } else {
        setInterim(lastInterim.trim())
      }
    }

    rec.onerror = () => {
      clearTimeout(timeoutRef.current)
      setListening(false)
      setInterim('')
    }

    rec.onend = () => {
      clearTimeout(timeoutRef.current)
      setListening(false)
      setInterim('')
      // Invio automatico del trascritto (come WhatsApp: rilascio = invia)
      if (finalTranscript.trim()) {
        sendMessage(finalTranscript.trim())
      }
    }

    rec.start()
  }, [listening, lang]) // eslint-disable-line

  const stopListening = useCallback(() => {
    clearTimeout(timeoutRef.current)
    recRef.current?.stop()
  }, [])

  // ── Key handler ──────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-sheet chat-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />

        {/* Header */}
        <div className="modal-header chat-header">
          <div className="chat-header-left">
            <Bot size={20} className="chat-header-icon" />
            <span className="modal-title">Mindesk Agent</span>
          </div>
          <div className="chat-header-actions">
            <button
              className="btn-icon"
              onClick={() => setShowHistory(!showHistory)}
              aria-label={t('agent_history')}
              title={t('agent_history')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8v4l3 3" /><circle cx="12" cy="12" r="10" />
              </svg>
            </button>
            <button className="btn-icon" onClick={toggleTts} aria-label={ttsEnabled ? t('agent_tts_off') : t('agent_tts_on')}>
              {ttsEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
            <button className="btn-icon" onClick={onClose} aria-label={t('close')}>
              <X size={20} />
            </button>
          </div>
        </div>

        {showHistory && (
          <ChatHistory
            conversations={conversations}
            activeId={conversationId}
            onSelect={handleLoadConversation}
            onNew={startNewConversation}
            onDelete={handleDeleteConversation}
          />
        )}

        <ChatMessages
          messages={messages}
          loading={loading}
          interim={interim}
          ttsEnabled={ttsEnabled}
          onSpeak={speak}
        />

        <ChatInput
          input={input}
          setInput={setInput}
          listening={listening}
          loading={loading}
          speechSupported={!!SpeechRecognitionAPI}
          onSend={send}
          onMicPressStart={startListening}
          onMicPressEnd={stopListening}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  )
}
